/**
 * View preferences for the discovery grid host (docs/DISCOVERY_GRID_REDESIGN_SPEC.md §4.5):
 * cell density and whether the legend is collapsed. Pure helpers + a tiny
 * localStorage wrapper so the host stays declarative and the rules are testable.
 */
import type { GridDensity } from '../components/DiscoveryGrid';

export type DensitySetting = 'auto' | GridDensity;

/** Auto density: small engagements get big, labeled cells; big ones get the dense map. */
export function resolveDensity(setting: DensitySetting, totalCells: number): GridDensity {
  if (setting !== 'auto') return setting;
  if (totalCells <= 48) return 'spacious';
  if (totalCells <= 200) return 'comfortable';
  return 'compact';
}

export interface GridViewPrefs {
  density: DensitySetting;
  legendCollapsed: boolean;
}

const KEY = 'pathkit.discoveryGrid.view';
const DEFAULTS: GridViewPrefs = { density: 'auto', legendCollapsed: false };
const DENSITIES: DensitySetting[] = ['auto', 'compact', 'comfortable', 'spacious'];

export function readGridViewPrefs(storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): GridViewPrefs {
  try {
    const raw = storage?.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return {
      density: DENSITIES.includes(parsed?.density) ? parsed.density : DEFAULTS.density,
      legendCollapsed: parsed?.legendCollapsed === true,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function writeGridViewPrefs(prefs: GridViewPrefs, storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): void {
  try {
    storage?.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* private mode or blocked storage: the preference just does not persist */
  }
}
